<script setup>
import {ref} from "vue";
import DialogModal from "@/Components/DialogModal.vue";
import {watch} from "vue";
import {onMounted} from "vue";

const props = defineProps({
  canSeeUploadForm: {
    type: Boolean,
    default: true
  },
  canModifyMediaDetails: {
    type: Boolean,
    default: true
  },
  hideSave: {
    type: Boolean,
    default: false
  },
  hideFiles: {
    type: Boolean,
    default: false
  },
  initialFiles: {
    type: Object,
    default: () => ({ I: [], F: [] })
  },
});

const emit = defineEmits(['update', 'delete']);
const files = ref({ I: [], F: [] });
const preview = ref(null);
const MAX_FILE_SIZE = 30 * 1024 * 1024;
const localError = ref(null);

const handleUpload = (event) => {
  const uploaded = Array.from(event.target.files);
  uploaded.forEach(file => {
    if (file.size > MAX_FILE_SIZE) {
      localError.value = `The file "${file.name}" exceeds the maximum allowed size of 30MB.`;
      return;
    }

    const type = file.type.startsWith("image/") ? 'I' : 'F';
    const fileData = {
      file,
      file_name: file.name,
      type,
      url: URL.createObjectURL(file),
    };
    files.value[type].push(fileData);
  });

  emit("update", files.value);
};

const dropArea = ref({active: false});

const dropMedia = (event) => {
  dropArea.value.active = false;
  const dropped = Array.from(event.dataTransfer.files);
  dropped.forEach(file => {
    if (file.size > MAX_FILE_SIZE) {
      localError.value = `The file "${file.name}" exceeds the maximum allowed size of 30MB.`;
      return;
    }

    const type = file.type.startsWith("image/") ? 'I' : 'F';
    const fileData = {
      file,
      file_name: file.name,
      type,
      url: URL.createObjectURL(file),
    };
    files.value[type].push(fileData);
  });
  emit("update", files.value);
};

const openPreviewModal = (image) => {
  preview.value = image;
};
const closePreviewModal = () => {
  preview.value = null;
};

const deleting = ref({ media: null, type: null, index: null, loading: false });

const openDeleteMediaModal = (type, index) => {
  deleting.value = {
    media: files.value[type][index],
    type,
    index,
    loading: false
  };
};

const closeDeleteMediaModal = () => {
  deleting.value = { media: null, type: null, index: null, loading: false };
};


const removeFile = (type, index) => {
  const media = files.value[type][index];

  if (media) {
    openDeleteMediaModal(type, index);
  } else {
    files.value[type].splice(index, 1);
    emit("update", files.value);
  }
};

watch(() => props.initialFiles, (newFiles) => {
  files.value = {
    I: [...newFiles.I],
    F: [...newFiles.F],
  };
}, { deep: true, immediate: true });

const confirmRemoveMedia = () => {
  const { media, type, index } = deleting.value;

  deleting.value.loading = true;

  emit('delete', media);

  files.value[type].splice(index, 1);
  emit("update", files.value);
  closeDeleteMediaModal();
};

onMounted(() => {
  files.value = {
    I: [...props.initialFiles.I],
    F: [...props.initialFiles.F]
  };
});

</script>

<template>
  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md mb-5">
    <div class="sm:grid grid-cols-6 gap-6" v-if="!hideSave && canSeeUploadForm">
      <div class="col-span-6">
        <div class="flex justify-center items-center w-full">
          <label
            @drop.prevent="dropMedia"
            @dragenter.prevent="dropArea.active = true"
            @dragover.prevent="dropArea.active = true"
            @dragleave.prevent="dropArea.active = false"
            class="flex flex-col justify-center items-center w-full h-32 rounded-lg border-2 border-dashed cursor-pointer hover:bg-gray-100"
            :class="dropArea.active ? ['border-gray-500', 'bg-gray-100'] : ['border-gray-300']"
          >
            <div class="flex flex-col justify-center items-center pt-5 pb-6">
              <svg aria-hidden="true" class="mb-3 w-10 h-10 text-gray-400" fill="none" stroke="currentColor"
                   viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
              </svg>
              <p class="mb-2 text-sm text-gray-500">
                <span class="font-semibold">Click to upload</span> or drag and drop
              </p>
              <p class="text-xs text-gray-500">Max. File Size: 30MB</p>
            </div>
            <input @change="handleUpload" type="file" multiple class="hidden">
          </label>
        </div>
      </div>
    </div>

    <p v-if="localError" class="text-red-600 text-sm mt-2">{{ localError }}</p>

    <!-- Images -->
    <p class="font-bold mt-5 mb-2">Images</p>
    <div class="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-3">
      <figure v-for="(media, index) in files.I" :key="index" class="relative cursor-pointer min-h-32">
        <figcaption class="bg-gray-50/50 w-full text-center mt-1 text-sm" v-text="media.file_name" />
        <img
          :src="media.url"
          :alt="media.file_name"
          class="h-auto max-w-full rounded-lg cursor-pointer"
          @click="openPreviewModal(media)"
        />
        <figcaption class="absolute bottom-2 bg-gray-50/50 w-full" v-if="canModifyMediaDetails">
          <div class="text-center mt-1 text-xl">
            <button type="button" class="fa fa-trash text-red-500" @click="removeFile('I', index)"></button>
          </div>
        </figcaption>
      </figure>
    </div>

    <!-- Files -->
    <hr class="mt-5 mb-5 text-gray-200" v-if="!hideFiles">
    <p class="font-bold mb-2"  v-if="!hideFiles">Files</p>
    <div class="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-3" v-if="!hideFiles">
      <figure v-for="(media, index) in files.F" :key="index" class="relative cursor-pointer min-h-32">
        <figcaption class="bg-gray-50/50 w-full text-center mt-1 text-sm" v-text="media.file_name" />
        <a
          :href="media.url"
          class="flex text-7xl bg-gray-300 cursor-pointer justify-center items-center h-44 max-w-full rounded-lg"
          :title="`Download ${media.file_name}`"
          target="_blank"
        >
          <span class="fa fa-file-download"></span>
        </a>
        <figcaption class="absolute bottom-2 bg-gray-50/50 w-full" v-if="canModifyMediaDetails">
          <div class="text-center mt-1 text-xl">
            <span class="fa fa-trash text-red-500 cursor-pointer" @click="removeFile('F', index)"></span>
          </div>
        </figcaption>
      </figure>
    </div>
  </div>

  <DialogModal :show="preview !== null" @close="closePreviewModal" max-width="7xl">
    <template #title>
      <b v-text="preview?.file_name" />
    </template>
    <template #content>
      <img :src="preview?.url" :alt="preview?.file_name" />
    </template>
  </DialogModal>

  <DialogModal :show="deleting.media !== null" @close="closeDeleteMediaModal">
    <template #title>
      <b>Please confirm</b>
    </template>

    <template #content>
      You are about to delete <b>{{ deleting.media?.file_name }}</b>.
      <br>Are you sure?
    </template>

    <template #footer>
      <button class="mr-2 px-4 py-2 text-gray-600 bg-gray-200 rounded" @click="closeDeleteMediaModal" :disabled="deleting.loading">
        Cancel
      </button>
      <button class="px-4 py-2 text-white bg-red-500 rounded" @click="confirmRemoveMedia" :disabled="deleting.loading">
        Confirm
      </button>
    </template>
  </DialogModal>

</template>


